import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { API } from "@/api";
import { toast } from "sonner";
import { Star, Download, AlertTriangle, TrendingUp, MessageSquare, Filter } from "lucide-react";

/**
 * FeedbackPanel — Admin dashboard block.
 * GET /api/dashboard/admin/feedback
 * GET /api/dashboard/admin/feedback/csv
 */
export default function FeedbackPanel({ api }) {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
      const { data } = await api.get(`/dashboard/admin/feedback?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("upchaar_token");
      const resp = await fetch(`${API}/dashboard/admin/feedback/csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "upchaar-feedback.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading && !data) return <Card className="medical-card p-5 text-sm text-slate-500">Loading feedback…</Card>;
  if (!data) return null;
  const o = data.overall || {};

  return (
    <Card className="medical-card p-4 sm:p-5" data-testid="admin-feedback-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          <h2 className="font-heading text-lg font-semibold text-slate-900">Patient feedback</h2>
          {data.low_rating_alert && (
            <Badge className="bg-rose-500/15 text-rose-700 border border-rose-200 hover:bg-rose-500/15 gap-1" data-testid="feedback-low-alert">
              <AlertTriangle className="w-3 h-3" /> {o.low_rating_count} low
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-36" data-testid="feedback-filter-from" />
          <span className="text-xs text-slate-400">→</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-36" data-testid="feedback-filter-to" />
          <Button size="sm" variant="outline" onClick={downloadCsv} className="gap-1 h-8" data-testid="feedback-csv-btn">
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Overall */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatTile icon={Star} label="Overall rating" value={o.avg_rating != null ? `${o.avg_rating.toFixed(1)} ★` : "—"} testid="feedback-overall-rating" />
        <StatTile icon={MessageSquare} label="Total reviews" value={o.review_count ?? 0} testid="feedback-total-reviews" />
        <StatTile icon={TrendingUp} label="Low ratings (≤2★)" value={o.low_rating_count ?? 0} accent={o.low_rating_count > 0 ? "rose" : "slate"} testid="feedback-low-count" />
      </div>

      {/* Per doctor */}
      {data.per_doctor && data.per_doctor.length > 0 && (
        <section className="mb-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Doctors</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.per_doctor.map((d) => (
              <div key={d.doctor_id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between" data-testid={`feedback-doctor-${d.doctor_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{d.doctor_name || d.doctor_id}</div>
                  <div className="text-xs text-slate-500">{d.review_count} review{d.review_count === 1 ? "" : "s"}</div>
                </div>
                <StarRow rating={d.avg_rating} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent comments (anonymised) */}
      {data.recent && data.recent.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Recent (anonymised)</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {data.recent.map((r, i) => (
              <div key={i} className="rounded border border-slate-100 bg-slate-50/40 px-3 py-2 flex items-start gap-3" data-testid={`feedback-recent-${i}`}>
                <div className="w-8 h-8 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center shrink-0">{r.initials}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <StarRow rating={r.rating} />
                    <span className="text-[11px] text-slate-400">{r.created_at?.slice(0, 10)}</span>
                  </div>
                  <div className="text-xs text-slate-500">Dr. {r.doctor_name}</div>
                  {r.comment && <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{r.comment}</div>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.recent?.length === 0 && (
        <p className="text-sm text-slate-500 py-6 text-center" data-testid="feedback-empty">No feedback yet in this filter.</p>
      )}
    </Card>
  );
}

function StatTile({ icon: Icon, label, value, accent, testid }) {
  const cls = accent === "rose"
    ? "bg-rose-50 border-rose-200 text-rose-700"
    : "bg-slate-50 border-slate-200 text-slate-700";
  return (
    <div className={`rounded-lg border p-3 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="font-heading text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

export function StarRow({ rating }) {
  if (rating == null) return <span className="text-xs text-slate-400">no rating yet</span>;
  const r = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" title={`${rating.toFixed?.(1) ?? rating} / 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= r ? "text-amber-500 fill-amber-400" : "text-slate-300"}`} />
      ))}
      <span className="text-xs font-semibold text-slate-700 ml-1">{Number(rating).toFixed(1)}</span>
    </span>
  );
}
